import React from "react";
import styled from "styled-components";
import type { TaxDeclaration } from "@/api/taxDeclaration";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";
import { phoneCountries } from "@/shared/utils/phoneCountries";

interface Props {
	declaration: TaxDeclaration | null;
	loading?: boolean;
	onManage?: () => void;
	/** "Required" framing for the terms-acceptance gate. */
	required?: boolean;
}

export const describeTaxDeclaration = (declaration: TaxDeclaration): { primary: string; secondary: string } => {
	const country = phoneCountries.find((c) => c.iso === declaration.countryCode)?.name ?? declaration.countryCode;
	const entity =
		declaration.entityType === "company" ? "Company" : declaration.entityType === "trust" ? "Trust" : "Individual";
	const registration = declaration.isTaxRegistered
		? `Tax registered${declaration.registrationNumberLast4 ? ` · number ending ${declaration.registrationNumberLast4}` : ""}`
		: "Not registered for tax";
	return { primary: declaration.legalName, secondary: `${entity} · ${country} · ${registration}` };
};

/** Summary of the live declaration with a manage action (profile page, Accept Terms page). */
export const TaxDetailsCard: React.FC<Props> = ({ declaration, loading = false, onManage, required = false }) => {
	const summary = declaration ? describeTaxDeclaration(declaration) : null;
	return (
		<Card $missing={!declaration && required}>
			<Copy>
				<Label>Tax details</Label>
				{loading ? (
					<Secondary>Loading…</Secondary>
				) : summary ? (
					<>
						<Primary>{summary.primary}</Primary>
						<Secondary>{summary.secondary}</Secondary>
					</>
				) : (
					<Secondary>
						{required ? "Required before you can accept campaign terms." : "No tax details on file."}
					</Secondary>
				)}
			</Copy>
			{onManage ? (
				<Action type="button" onClick={onManage} disabled={loading}>
					{declaration ? "Update" : "Add tax details"}
				</Action>
			) : null}
		</Card>
	);
};

const Card = styled.div<{ $missing: boolean }>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 16px;
	border-radius: 12px;
	background: #fff;
	border: 1px solid ${({ $missing }) => ($missing ? "#ffb4b4" : "#dde2fa")};
`;
const Copy = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
`;
const Label = styled.span`
	color: #707070;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.04em;
	text-transform: uppercase;
`;
const Primary = styled.span`
	color: #303030;
	font-size: 15px;
	font-weight: 600;
`;
const Secondary = styled.span`
	color: #565656;
	font-size: 13px;
`;
const Action = styled.button`
	flex-shrink: 0;
	border: 0;
	background: transparent;
	color: ${CUSTOM_COLORS.SECONDARY};
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	padding: 0;
	&:disabled {
		opacity: 0.6;
		cursor: default;
	}
`;
